import { Button } from '@/components/ui/design-system';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { H4, P3 } from '@/components/ui/text';
import { useTeamSubscriptionData } from '@/hooks/api/team';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { useCallback, useEffect, useState } from 'react';
import type { ContactView } from '../queries/use-contact-views';

type AccessType = 'USER' | 'TEAM';

interface SaveViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string, access: AccessType) => void;
  onDuplicate?: (viewId: string, name: string, access: AccessType) => void;
  viewToDuplicate?: ContactView | null;
  isLoading?: boolean;
}

export function SaveViewDialog({
  open,
  onOpenChange,
  onSave,
  onDuplicate,
  viewToDuplicate,
  isLoading = false,
}: SaveViewDialogProps) {
  const { t } = useTranslation('desktop_ui');
  const [name, setName] = useState('');
  const [access, setAccess] = useState<AccessType>('USER');
  const { totalSeats, isLoading: isTeamLoading } = useTeamSubscriptionData();
  const hasTeam = totalSeats > 0;

  const isDuplicateMode = !!viewToDuplicate;

  useEffect(() => {
    if (open && viewToDuplicate) {
      setName(`${viewToDuplicate.name} (copy)`);
      setAccess(((viewToDuplicate as any).owner_type as AccessType) ?? 'USER');
    } else if (!open) {
      setName('');
      setAccess('USER');
    }
  }, [open, viewToDuplicate]);

  const handleSave = useCallback(() => {
    if (name.trim()) {
      if (isDuplicateMode && viewToDuplicate && onDuplicate) {
        onDuplicate(viewToDuplicate.id, name.trim(), access);
      } else {
        onSave(name.trim(), access);
      }
    }
  }, [name, access, onSave, onDuplicate, isDuplicateMode, viewToDuplicate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && name.trim() && !isLoading) {
        handleSave();
      }
    },
    [handleSave, name, isLoading],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[400px] overflow-hidden rounded-2xl p-0 gap-0"
        showCloseIcon={false}
      >
        <DialogHeader className="gap-1 px-4 py-3">
          <DialogTitle>
            <H4 className="text-primary-100">
              {isDuplicateMode
                ? t('components.contacts.views.dialog.duplicateTitle')
                : t('components.contacts.views.dialog.createTitle')}
            </H4>
          </DialogTitle>
          <DialogDescription>
            <P3 className="text-primary-60">
              {isDuplicateMode
                ? t('components.contacts.views.dialog.duplicateDescription')
                : t('components.contacts.views.dialog.createDescription')}
            </P3>
          </DialogDescription>
        </DialogHeader>
        <div className="px-4 pb-3">
          <label className="mb-3 block">
            <P3 className="mb-1 text-primary-60">
              {t('components.contacts.views.dialog.nameLabel')}
            </P3>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('components.contacts.views.dialog.namePlaceholder')}
              className="w-full rounded-md border border-primary-10 bg-white px-3 py-2 font-inter text-s text-primary-100 outline-none placeholder:text-primary-40 focus:border-primary-40"
            />
          </label>
          {hasTeam && (isDuplicateMode || !isTeamLoading) && (
            <div>
              <P3 className="mb-1 text-primary-60">
                {t('components.contacts.views.dialog.accessLabel')}
              </P3>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAccess('USER')}
                  className={cn(
                    'flex h-8 flex-1 cursor-pointer items-center justify-center rounded-md border px-3 transition-colors',
                    access === 'USER'
                      ? 'border-highlight-default bg-highlight-default/10 text-highlight-default'
                      : 'border-primary-10 bg-white text-primary-60 hover:bg-primary-5',
                  )}
                >
                  <P3 className={access === 'USER' ? 'font-medium' : ''}>
                    {t('components.contacts.views.dialog.accessOnlyMe')}
                  </P3>
                </button>
                <button
                  type="button"
                  onClick={() => setAccess('TEAM')}
                  className={cn(
                    'flex h-8 flex-1 cursor-pointer items-center justify-center rounded-md border px-3 transition-colors',
                    access === 'TEAM'
                      ? 'border-highlight-default bg-highlight-default/10 text-highlight-default'
                      : 'border-primary-10 bg-white text-primary-60 hover:bg-primary-5',
                  )}
                >
                  <P3 className={access === 'TEAM' ? 'font-medium' : ''}>
                    {t('components.contacts.views.dialog.accessTeam')}
                  </P3>
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-primary-5 p-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {t('components.contacts.views.dialog.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!name.trim() || isLoading}
            isLoading={isLoading}
          >
            {t('components.contacts.views.dialog.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
